import { useEffect } from 'react';
import { SITE_DESCRIPTION, SITE_NAME, SITE_URL, toAbsoluteUrl } from '../seo';

function setMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!content) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
}

function setStructuredData(data) {
  const id = 'seo-structured-data';
  let element = document.getElementById(id);

  if (!data) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
}

function Seo({
  title = `${SITE_NAME} | Artist-run art space in London`,
  description = SITE_DESCRIPTION,
  canonicalUrl = `${SITE_URL}/`,
  imageUrl,
  imageAlt = `${SITE_NAME}, artist-run art space in London`,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  structuredData
}) {
  const absoluteImageUrl = toAbsoluteUrl(imageUrl);
  const structuredDataJson = structuredData ? JSON.stringify(structuredData) : '';

  useEffect(() => {
    document.documentElement.lang = 'en-GB';
    document.title = title;
    setCanonical(canonicalUrl);

    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('name', 'googlebot', robots);

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', 'en_GB');
    setMeta('property', 'og:image', absoluteImageUrl);
    setMeta('property', 'og:image:secure_url', absoluteImageUrl);
    setMeta('property', 'og:image:alt', absoluteImageUrl ? imageAlt : '');

    setMeta('name', 'twitter:card', absoluteImageUrl ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', absoluteImageUrl);
    setMeta('name', 'twitter:image:alt', absoluteImageUrl ? imageAlt : '');

    setStructuredData(structuredDataJson ? JSON.parse(structuredDataJson) : null);
  }, [
    absoluteImageUrl,
    canonicalUrl,
    description,
    imageAlt,
    robots,
    structuredDataJson,
    title
  ]);

  return null;
}

export default Seo;
